import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom-v5-compat";
import z from "zod";

import { isApiResponseError } from "@common/schemas/GenericErrorSchema";

import { Button, Price, PriceLine } from "@onsite_app/MobileOrderingMenu/Atoms";
import { TextField } from "@onsite_app/MobileOrderingMenu/Atoms/Form";
import { LoadingSpinner } from "@onsite_app/MobileOrderingMenu/Loading";
import { useSession } from "@onsite_app/MobileOrderingMenu/Pages/Session/SessionContext";
import { useVenue } from "@onsite_app/MobileOrderingMenu/VenueContext";

import { VoucherCardPreview } from "../../VoucherCart/VoucherCartItems/VoucherCardPreview";
import { useCreateCartVoucherAssignment, useVoucherLookup } from "./mutations";
import { Voucher } from "./schema";

const FoundVoucher = ({ voucher }: { voucher: Voucher }) => {
  return <VoucherCardPreview voucher={voucher} />;
};
const VoucherCodeFormSchema = z.object({
  code: z.string().min(1),
});

const ErrorMessage = ({ error }: { error: unknown }) => {
  const { t } = useTranslation("translations", {
    keyPrefix: "voucherAssignment.errors",
  });

  if (isApiResponseError(error)) {
    switch (error.name) {
      case "VoucherLookupError":
        return t("unableToFindVoucher");
      case "RateLimitExceeded":
        return t("rateLimitExceeded");
      case "VoucherInUseException":
        return t("voucherAlreadyInUse");
      default:
        return (
          <>
            {error.name} - {t("unknownError")}
          </>
        );
    }
  }
  return t("unknownError");
};

export type VoucherCodeForm = z.infer<typeof VoucherCodeFormSchema>;
const LookupVoucherCode = () => {
  const { t } = useTranslation();
  const formProps = useForm({
    resolver: zodResolver(VoucherCodeFormSchema),
  });
  const lookupVoucherCodeMutation = useVoucherLookup();
  const createCartVoucherAssignmentMutation = useCreateCartVoucherAssignment();
  const [voucherTimedOut, setVoucherTimedOut] = useState(false);
  const onSubmit = async (data: VoucherCodeForm) => {
    lookupVoucherCodeMutation.mutate(data);
    setVoucherTimedOut(false);
  };
  const venue = useVenue();

  const cart = useSession().cart;
  const navigate = useNavigate();
  const onUseThisVoucher = async () => {
    if (!lookupVoucherCodeMutation.data) {
      console.error("No voucher to use");
      return;
    }
    await createCartVoucherAssignmentMutation.mutateAsync({
      cartUuid: cart.uuid,
      voucherToken: lookupVoucherCodeMutation.data.voucherToken,
      amountInCents: lookupVoucherCodeMutation.data.voucher.balanceInCents,
    });
    navigate("..");
  };

  const voucherTokenExpiresAt =
    lookupVoucherCodeMutation.data?.voucherTokenExpiresAt;
  useEffect(() => {
    if (!voucherTokenExpiresAt) return;
    const now = Math.floor(Date.now() / 1000);
    setTimeout(
      () => {
        // Expired
        lookupVoucherCodeMutation.reset();
        setVoucherTimedOut(true);
      },
      (voucherTokenExpiresAt - now) * 1000,
    );
  }, [voucherTokenExpiresAt]);
  return (
    <div className="space-y-32">
      <h3 className="text-center text-2xl font-bold">
        {t("voucherAssignment.title")}
      </h3>
      {(lookupVoucherCodeMutation.isIdle ||
        lookupVoucherCodeMutation.isError) && (
        <form
          className="space-y-32"
          onSubmit={formProps.handleSubmit(onSubmit)}
        >
          <div>
            {t("voucherAssignment.description", { venueName: venue.name })}
          </div>
          <div className="space-y-8">
            <TextField
              label={t("voucherAssignment.voucherCodeLookup")}
              formProps={formProps}
              name="code"
            />
            {voucherTimedOut && (
              <div className="mb-16 text-venue-highlight">
                {t("voucherAssignment.voucherTimedOut")}
              </div>
            )}
            {lookupVoucherCodeMutation.isError && (
              <div className="text-xl text-venue-highlight">
                <ErrorMessage error={lookupVoucherCodeMutation.error} />
              </div>
            )}
          </div>
          <Button primary>{t("voucherAssignment.lookupCode")} </Button>
        </form>
      )}
      {lookupVoucherCodeMutation.isPending && (
        <div className="flex flex-col items-center space-y-16">
          <LoadingSpinner size={"32"} />
          <div>{t("voucherAssignment.lookingUpCode")}</div>
        </div>
      )}
      {lookupVoucherCodeMutation.isSuccess && (
        <div className="space-y-16">
          <FoundVoucher voucher={lookupVoucherCodeMutation.data.voucher} />{" "}
          <div className="space-y-16">
            <PriceLine
              label={t("voucherAssignment.remainingvoucherValue")}
              value={
                <Price
                  value={lookupVoucherCodeMutation.data.voucher.balanceInCents}
                  fromCents
                />
              }
            />
            <Button
              primary
              mutation={createCartVoucherAssignmentMutation}
              onClick={onUseThisVoucher}
            >
              {t("voucherAssignment.useThisVoucher")}{" "}
            </Button>
            {createCartVoucherAssignmentMutation.isError && (
              <div className="text-xl text-venue-highlight">
                <ErrorMessage
                  error={createCartVoucherAssignmentMutation.error}
                />
              </div>
            )}

            <a
              className="block text-center"
              onClick={lookupVoucherCodeMutation.reset}
            >
              {t("voucherAssignment.notTheRightVoucherUseAnother")}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default LookupVoucherCode;
